import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api.js';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { AlertTriangle } from 'lucide-react';

// THE PEOPLE DOORS, ONE SWITCH EACH (review B-R6, lane B of V3 Overnight 3).
//
// Every door that meets a person can ask the one people match
// (services/v3/people-doors.service.js). Each door has its own switch in
// admin_settings, v3.people_match.<door>: off (today's code), shadow (the match
// runs read-only and the Render log says what it WOULD do), on (the door acts).
// "Switches should exist in TRACON, not in Render" (CLAUDE.md Automation
// Control), and a door ships with its handle: this is the handle.
//
// An absent row reads as off, the same as a row set to off. The page writes
// through PATCH /api/tracon/admin/admin-settings (the broker-token mount) and then READS the value
// back before it says anything (a confirmation is a read, not an echo).

const PREFIX = 'v3.people_match.';

// The door list matches DOORS in services/v3/people-doors.service.js; the guard
// tests/v3-people-doors.test.js holds the two lists equal.
const DOORS = [
  { door: 'phone_intake', label: 'Phone intake', on: 'A known pilot is used as is and gets no transcribed name, email or phone. Nobody known for sure: no Contact and no Account are minted; a suggestion holds what was heard.' },
  { door: 'zoom_call', label: 'Zoom calls', on: 'A known caller tags the call row. An unknown caller on an intake call becomes a suggestion.' },
  { door: 'zoom_sms', label: 'Zoom texts', on: 'A known sender tags the text. A shared or unknown number stays untagged with a suggestion, unless you filed that number to an account.' },
  { door: 'phf', label: 'Pilot history forms', on: 'The pilot\'s own form creates their person when nobody matches, linked to the account as pilot. A form with no Contact yet creates nobody.' },
  { door: 'intake_form', label: 'Intake forms', on: 'The applicant\'s own self or kiosk form may create their person. Anyone else named on it, or typed in broker mode, becomes a suggestion.' },
  { door: 'resend_inbound', label: 'Inbound mail (Resend)', on: 'The sender is matched and the message filed by thread, tail or policy number. An unknown sender becomes a suggestion. Needs the v3 mail branch.' },
];
const MODES = [
  { mode: 'off', label: 'Off', note: 'Today\'s code. The match does not run.' },
  { mode: 'shadow', label: 'Shadow', note: 'The match runs read-only. Nothing is written; the Render log says what it would do.' },
  { mode: 'on', label: 'On', note: 'The door acts on the match.' },
];
const modeOf = (v) => (MODES.some((m) => m.mode === v) ? v : 'off');

function DoorRow({ d, current, onSaved }) {
  const [pick, setPick] = useState(current);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPick(current); }, [current]);
  const key = `${PREFIX}${d.door}`;

  async function save() {
    setSaving(true);
    try {
      const res = await api.patch('/api/tracon/admin/admin-settings', { updates: { [key]: pick } });
      if (res && res.errors && res.errors.length) throw new Error(res.errors[0].error || 'the switch was not written');
      let read;
      try {
        read = await api.get(`/api/tracon/admin/admin-settings/${encodeURIComponent(key)}?_t=${Date.now()}`);
      } catch {
        toast.info(`${d.label}: saved, but the switch could not be read back. Reload to check it.`);
        onSaved();
        return;
      }
      const now = modeOf(read && read.value);
      if (now === pick) toast.success(`${d.label} is now ${now}.`);
      else toast.error(`${d.label} reads ${now} after the save, not ${pick}.`);
      onSaved();
    } catch (e) {
      toast.error(e?.body?.error || e.message || 'Could not set the switch');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-card border border-border bg-surface p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-text">{d.label}</span>
        <span className="text-[12px] text-text-muted">now {current}</span>
        <span className="ml-auto font-mono text-[11px] text-text-dim">{key}</span>
      </div>
      <p className="mt-1 text-[12.5px] text-text-muted">When on: {d.on}</p>
      <div className="mt-2.5 flex flex-wrap items-center gap-2" role="radiogroup" aria-label={`${d.label} switch`}>
        {MODES.map((m) => (
          <button
            key={m.mode}
            type="button"
            role="radio"
            aria-checked={pick === m.mode}
            title={m.note}
            onClick={() => setPick(m.mode)}
            className={[
              'h-9 min-w-[4.5rem] rounded-card border px-3 text-sm',
              pick === m.mode ? 'border-accent bg-accent text-white' : 'border-border text-text hover:border-accent',
            ].join(' ')}
          >
            {m.label}
          </button>
        ))}
        <Button onClick={save} disabled={saving || pick === current} className="h-9">
          {saving ? 'Saving…' : pick === current ? 'Saved' : `Set ${pick}`}
        </Button>
      </div>
    </div>
  );
}

export default function PeopleDoorsAdmin() {
  const [values, setValues] = useState(null);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      const d = await api.get(`/api/tracon/admin/admin-settings?prefix=${encodeURIComponent(PREFIX)}&_t=${Date.now()}`);
      setValues(d && d.settings && typeof d.settings === 'object' ? d.settings : {});
      setError('');
    } catch (e) {
      setError(e.message || 'Could not read the switches');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-lg font-semibold text-text">People doors</h1>
        <p className="mt-1 max-w-2xl text-[13px] text-text-muted">
          Each door that meets a person can ask the one people match before it touches a Zoho
          Contact. Off is today&apos;s code. Shadow runs the match read-only so you can compare it
          with today&apos;s answer in the Render log. On lets the door act. A switch with no row
          reads as off.
        </p>
      </div>
      {error ? (
        <p className="flex items-center gap-2 text-sm text-danger">
          <AlertTriangle size={14} /> {error}
        </p>
      ) : values === null ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : (
        <div className="space-y-2">
          {DOORS.map((d) => (
            <DoorRow key={d.door} d={d} current={modeOf(values[`${PREFIX}${d.door}`])} onSaved={refresh} />
          ))}
        </div>
      )}
    </div>
  );
}
